import { z } from 'zod';

export interface PublicProjectCanonicalPolicy {
  publisherSlug: string;
  projectSlugs: readonly string[];
  listingOrigin: string;
}

// The listing renderer and sitemap must use the same cross-portal policy.
export const PUBLIC_PROJECT_CANONICAL_POLICIES: readonly PublicProjectCanonicalPolicy[] =
  [
    {
      publisherSlug: 'maxima-cabida',
      projectSlugs: ['punta-del-sur-etapa-1-pds-e1'],
      listingOrigin: 'https://viviendasocial.pe',
    },
  ];

const PUBLIC_SITE_CONTACT_EMAILS = {
  'inmueble.red': 'hola@inmueble.red',
  'puntadelsur.pe': 'hola@puntadelsur.pe',
  'viviendasocial.pe': 'hola@viviendasocial.pe',
} as const;

export function resolvePublicSiteContactEmail(hostname: string): string | null {
  const normalizedHostname = hostname.trim().toLowerCase();
  for (const [domain, email] of Object.entries(PUBLIC_SITE_CONTACT_EMAILS)) {
    if (
      normalizedHostname === domain ||
      normalizedHostname.endsWith(`.${domain}`)
    ) {
      return email;
    }
  }
  return null;
}

export const PUBLIC_MARKETPLACE_SITE_IDS = [
  'inmueble_red',
  'vivienda_social',
] as const;

export const publicMarketplaceSiteIdSchema = z.enum(
  PUBLIC_MARKETPLACE_SITE_IDS
);

export type PublicMarketplaceSiteId =
  (typeof PUBLIC_MARKETPLACE_SITE_IDS)[number];

export interface PublicMarketplaceSiteDefinition {
  id: PublicMarketplaceSiteId;
  name: string;
  rootHostname: string;
  contactEmail: string;
  routedHostnames: readonly string[];
  canonicalOrigin: string;
  defaultCatalogPath: string;
  catalogPolicy: 'general' | 'social_housing_only';
  publisherPolicy: 'open_publication' | 'operator_allowlist';
  endorsement: string | null;
}

export const INMUEBLE_RED_MARKETPLACE_SITE: PublicMarketplaceSiteDefinition =
  Object.freeze({
    id: 'inmueble_red',
    name: 'Inmueble.red',
    rootHostname: 'inmueble.red',
    contactEmail: PUBLIC_SITE_CONTACT_EMAILS['inmueble.red'],
    routedHostnames: ['inmueble.red', 'www.inmueble.red'],
    canonicalOrigin: 'https://inmueble.red',
    defaultCatalogPath: '/peru/',
    catalogPolicy: 'general',
    publisherPolicy: 'open_publication',
    endorsement: null,
  });

export const VIVIENDA_SOCIAL_MARKETPLACE_SITE: PublicMarketplaceSiteDefinition =
  Object.freeze({
    id: 'vivienda_social',
    name: 'ViviendaSocial.pe',
    rootHostname: 'viviendasocial.pe',
    contactEmail: PUBLIC_SITE_CONTACT_EMAILS['viviendasocial.pe'],
    routedHostnames: ['viviendasocial.pe', 'www.viviendasocial.pe'],
    canonicalOrigin: 'https://viviendasocial.pe',
    defaultCatalogPath: '/peru/',
    catalogPolicy: 'social_housing_only',
    publisherPolicy: 'operator_allowlist',
    endorsement: 'Una plataforma de Inmueble.red',
  });

export const PUBLIC_MARKETPLACE_SITES: readonly PublicMarketplaceSiteDefinition[] =
  Object.freeze([
    INMUEBLE_RED_MARKETPLACE_SITE,
    VIVIENDA_SOCIAL_MARKETPLACE_SITE,
  ]);

const PUBLIC_MARKETPLACE_SITE_BY_ID: ReadonlyMap<
  PublicMarketplaceSiteId,
  PublicMarketplaceSiteDefinition
> = new Map(PUBLIC_MARKETPLACE_SITES.map((site) => [site.id, site]));

const PUBLIC_MARKETPLACE_SITE_BY_HOSTNAME: ReadonlyMap<
  string,
  PublicMarketplaceSiteDefinition
> = new Map(
  PUBLIC_MARKETPLACE_SITES.flatMap((site) =>
    site.routedHostnames.map((hostname) => [hostname, site] as const)
  )
);

export function normalizePublicMarketplaceHostname(hostname: string): string {
  return hostname.split(':')[0]?.trim().toLowerCase() ?? '';
}

export function getPublicMarketplaceSiteById(
  siteId: PublicMarketplaceSiteId
): PublicMarketplaceSiteDefinition {
  const site = PUBLIC_MARKETPLACE_SITE_BY_ID.get(siteId);
  if (!site) {
    throw new Error(`Unknown public marketplace site: ${siteId}`);
  }
  return site;
}

export function resolvePublicMarketplaceSiteByHostname(
  hostname: string
): PublicMarketplaceSiteDefinition | null {
  return (
    PUBLIC_MARKETPLACE_SITE_BY_HOSTNAME.get(
      normalizePublicMarketplaceHostname(hostname)
    ) ?? null
  );
}

export function isPublicMarketplaceSiteId(
  value: string
): value is PublicMarketplaceSiteId {
  return PUBLIC_MARKETPLACE_SITE_IDS.includes(value as PublicMarketplaceSiteId);
}
